import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { NavLink, useMatch, useNavigate } from 'react-router-dom';

import BamLogo from '@/assets/icons/BamLogo';
import DarkLight from '@/assets/icons/DarkLight';
import Map from '@/assets/icons/Map';
import { PermissionGate, useBamAuth, useHasPermission } from '@/auth';
import { AdditionalOptionsDialog } from '@/components/navBar/AdditionalOptions/AdditionalOptionsDialog';
import { MoreInformationDialog } from '@/components/navBar/MoreInformation/MoreInformationDialog';
import { ThemeDialog } from '@/components/navBar/MoreInformation/themeDialog';
import { ThemeOption, useTheme } from '@/contextProviders/themeProvider';
import { useFetchWithAuth } from '@/queries/useFetchWithAuth';
import useClientState from '@/state/useClientState';
import { envVariables } from '@/utils/env';
import useSearchAndSidebarWerklijnen from '@/views/mwdView/useSearchAndSidebarWerklijnen';

import SearchBar from '../search/SearchBar';
import SearchFeatureToggle from '../search/v2/components/SearchFeatureToggle/SearchFeatureToggle';

import AuthStatus from './AuthStatus';

import style from './Navbar.module.css';

export type LinkEntry = {
  url: string;
  text: string;
};

type NavBarProps = {
  menuItems?: LinkEntry[];
};

function Navbar({ children, menuItems }: React.PropsWithChildren<NavBarProps>) {
  const setShowDialog = useClientState((state) => state.setShowDialog);
  const { performSearchRequest, setShowAdvancedSearchDialog, isFetching, clearSearchField } =
    useSearchAndSidebarWerklijnen();
  const { isLoggedIn } = useBamAuth();
  const isAllowedToOpstelCapaciteit = useHasPermission('mwd.opstelcapaciteit');
  const { fetchWithAuth } = useFetchWithAuth();
  const { featureBakkenteller, branchName, imageBuildNumber } = envVariables();
  const { themeMode, setThemeMode } = useTheme();
  const isAcceptatieOmgeving =
    window.location.hostname.includes('acceptatie') ||
    window.location.hostname.includes('materieela') ||
    import.meta.env.MODE === 'proxy-acceptatie' ||
    import.meta.env.MODE === 'proxy-test';
  const isProductieOmgeving =
    !import.meta.env.DEV &&
    !isAcceptatieOmgeving &&
    !window.location.hostname.includes('materieelt') &&
    !window.location.hostname.includes('test');
  const logoTitle = isProductieOmgeving ? undefined : `${branchName} (${imageBuildNumber})`;
  const matchMwdPath = useMatch('/');
  const navigate = useNavigate();

  const [showThemeDialog, setShowThemeDialog] = useState(false);
  const longPressTimeout = useRef<NodeJS.Timeout | null>(null);
  const easterEggClicks = useRef<number[]>([]);
  const fetchWithAuthRef = useRef(fetchWithAuth);
  fetchWithAuthRef.current = fetchWithAuth;

  const handleThemeDialogOpen = () => setShowThemeDialog(true);
  const handleThemeDialogClose = () => setShowThemeDialog(false);

  const handleThemeDialogSelect = (theme: 'light' | 'dark' | 'high-contrast-dark') => {
    setThemeMode(theme);
  };

  const handleNavigateHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const wasLongPress = useRef(false);

  const handleMouseDown = () => {
    wasLongPress.current = false;
    longPressTimeout.current = setTimeout(() => {
      wasLongPress.current = true;
      handleThemeDialogOpen();
    }, 600);
  };

  const handleMouseUp = () => {
    if (longPressTimeout.current) {
      clearTimeout(longPressTimeout.current);
      longPressTimeout.current = null;
    }
  };

  const handleClick = () => {
    if (!wasLongPress.current && !showThemeDialog) {
      handleThemeSwitch();
    }
  };

  const handleThemeSwitch = useCallback(() => {
    if (!longPressTimeout.current) {
      setThemeMode(themeMode === 'light' ? 'dark' : 'light');
    }
  }, [themeMode]);

  const handleLogoContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    if (document.getElementById('bam-over-root')) return;
    const now = Date.now();
    easterEggClicks.current = easterEggClicks.current.filter((t) => now - t < 2000);
    easterEggClicks.current.push(now);
    if (easterEggClicks.current.length >= 3) {
      easterEggClicks.current = [];
      const container = document.createElement('div');
      container.id = 'bam-over-root';
      document.body.appendChild(container);
      const root = createRoot(container);
      import('@/views/bamOver/BamOverView')
        .then(({ default: BamOverView }) => {
          if (!document.body.contains(container)) return;
          root.render(
            <BamOverView
              onClose={() => {
                root.unmount();
                container.remove();
              }}
              authFetch={(query: string, init?: RequestInit) => fetchWithAuthRef.current(query, init)}
            />,
          );
        })
        .catch(() => {
          container.remove();
        });
    }
  }, []);

  useEffect(() => {
    if (localStorage.getItem('theme')) {
      setThemeMode(localStorage.getItem('theme') as ThemeOption);
    }
  }, []);

  return (
    <div
      className={style.navBar}
      style={{
        backgroundColor: isAcceptatieOmgeving
          ? 'var(--tractie-color-background-accent-orange-subtle)'
          : 'var(--tractie-color-background-elevated-2)',
      }}
      data-acceptatie={isAcceptatieOmgeving ? 'yes' : 'no'}
      data-dd-action-name='NavBar'
    >
      <div
        className={style.navBarContainer}
        style={{ color: 'var(--tractie-color-content-emphasis)', zIndex: 1 }}
        data-dd-action-name='NavBarContainer'
      >
        <div
          className={style.navBarLogoContainer}
          style={{ left: '32px' }}
          onClick={handleNavigateHome}
          onContextMenu={handleLogoContextMenu}
          data-dd-action-name='NavBarLogo'
          title={logoTitle}
        >
          <div
            className={style.navBarLogo}
            style={{
              borderRadius: '6px',
              border: `1px solid var(--tractie-color-border-logo)`,
              backgroundColor: 'var(--tractie-color-background-brand-primary)',
            }}
          >
            <BamLogo
              fill='var(--tractie-color-content-logo)'
              height='40px'
              width='40px'
              aria-label='BamLogo'
              data-testid='BamLogo'
            />
          </div>
          <div
            className={style.navBarAppName}
            style={{ fontWeight: 600, color: 'var(--tractie-color-content-emphasis)' }}
          >
            BAM
          </div>
        </div>
        <ul className={style.navBarLinksList}>
          {menuItems?.map((i) => {
            const isActive = Boolean(
              location.pathname === i.url || location.pathname.substring(1).split('/')?.[0] === i.url.substring(1),
            );
            return (
              <li
                key={i.url}
                className={[style.navBarLinksListItem, isActive ? style.navBarLinksListItemActive : ''].join(' ')}
                style={isActive ? { color: 'var(--tractie-color-border-selected-default)' } : undefined}
              >
                <span className={style.menuLabelContainer} style={{ color: 'var(--tractie-color-content-emphasis)' }}>
                  <NavLink
                    to={i.url}
                    className={({ isActive }) => (isActive ? 'active' : '')}
                    style={{ color: 'var(--tractie-color-content-emphasis)', textDecoration: 'none' }}
                  >
                    {i.text}
                  </NavLink>
                </span>
              </li>
            );
          })}
        </ul>
        {matchMwdPath && isLoggedIn && (
          <div className={style.centered}>
            <SearchBar
              loading={isFetching}
              onUserSearchIntent={performSearchRequest}
              handleOpenAdvancedSearchDialog={() => setShowAdvancedSearchDialog(true)}
              clearSearchField={clearSearchField}
            />
          </div>
        )}
        <div className={style.navBarLoginContainer}>
          {matchMwdPath && isLoggedIn && (
            <div className={style.navBarOptional}>
              <SearchFeatureToggle />
            </div>
          )}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px' }}>
            {isAllowedToOpstelCapaciteit && featureBakkenteller && (
              <Map
                className={style.navBarDarkModeIcon}
                style={{ fontSize: '24px', cursor: 'pointer' }}
                data-dd-action-name='Open bakkenteller'
                onClick={() => {
                  void setShowDialog('bakkenteller', 'navbar');
                }}
              />
            )}
            {menuItems && menuItems.length > 0 && <MoreInformationDialog />}
            <div
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onClick={handleClick}
              style={{ lineHeight: '24px' }}
              data-dd-action-name='Thema wisselen'
            >
              <DarkLight className={style.navBarDarkModeIcon} data-testid='darkModeSwitch' height='24px' width='24px' />
              <ThemeDialog
                selectedTheme={themeMode}
                isOpen={showThemeDialog}
                onClose={handleThemeDialogClose}
                onSelectTheme={handleThemeDialogSelect}
              />
            </div>
            <PermissionGate anyOf={['beheer.manage', 'beheer.matbeheer', 'beheer.solver', 'beheer.tam', 'lmm.view']}>
              <AdditionalOptionsDialog />
            </PermissionGate>
            <AuthStatus />
          </div>
        </div>
      </div>
      <div className={style.navBarCenteredElements} style={{ maxWidth: 'calc(100vw / 2.4)' }}>
        {children}
      </div>
    </div>
  );
}

export default Navbar;
